import { RadioLayout } from "@tree-company/tree-query";
import { PageComponent } from "./components/page_component";
import { apiClient } from "./client";
import translate, { LANGUAGE } from "./locales";
import { NextStatements } from "./components/next_statements";
import { MoreInfoLink } from "./components/more_info_link";
import { StatementOpinionQuestion } from "./components/statement_opinion_question";
import { STATEMENT_KEYS, STATEMENTS } from "./data/statements";

function t(key_or_keys) {
  return { [LANGUAGE]: translate(key_or_keys) };
}

let aggregatorURL = import.meta.env.VITE_AGGREGATOR_URL;
if (aggregatorURL === undefined && import.meta.env.DEV) {
  aggregatorURL = "http://localhost:3300";
}

function getPreviousAnswer(key) {
  // We select the second to last one, since the last treesponse is the current one
  return apiClient.treesponses.at(-2)?.getAnswer(key);
}
const OPTION_KEYS = ["1", "2", "3", "4", "5"];
const aiResponseTexts = {
  proficiencyOkToxicityOk: t("ai_feedback.proficiency_ok_toxicity_ok"),
  proficiencyOkToxicityNotOk: t("ai_feedback.proficiency_ok_toxicity_not_ok"),
  proficiencyHighToxicityOk: t("ai_feedback.proficiency_high_toxicity_ok"),
  proficiencyHighToxicityNotOk: t(
    "ai_feedback.proficiency_high_toxicity_not_ok",
  ),
  proficiencyLowToxicityOk: t("ai_feedback.proficiency_low_toxicity_ok"),
  proficiencyLowToxicityNotOk: t("ai_feedback.proficiency_low_toxicity_not_ok"),
};

export function surveySettingsForStatement(statement) {
  let likeOptionsCount = null;
  const answeredStatements = apiClient.treesponses
    .filter((s) => s.completed)
    .map((s) => s.getAnswer("statement"));
  const availableStatements = STATEMENT_KEYS.filter(
    (s) => !answeredStatements.includes(s) && statement !== s,
  ).sort((s1, s2) => STATEMENTS[s1].order - STATEMENTS[s2].order);

  const statementTitle = t(["statements", statement, "statement"]);
  const closedQuestion = t(["statements", statement, "closed_question"]);
  const commentTitle = t(["statements", statement, "comment", "title"]);
  const commentDescription = t([
    "statements",
    statement,
    "comment",
    "description",
  ]);
  return {
    key: `csmart-${statement}`,
    pages: [
      {
        key: "opinion",
        order: 1,
        component: PageComponent,
        showNext: false,
        statement,
        title: statementTitle,
        htmlAttributes: {
          class: "survey--compact survey--opinion",
        },
        sections: [
          {
            type: "radio",
            component: StatementOpinionQuestion,
            htmlAttributes: {
              class: "question--statement",
            },
            order: 1,
            key: "opinion",
            layout: RadioLayout.buttons,
            description: closedQuestion,
            options: OPTION_KEYS.map((option) => ({
              label: t(["statementQuestion", "options", option, "text"]),
              value: option,
            })),
          },
        ],
      },
      {
        key: `comment-${statement}`,
        order: 3,
        component: PageComponent,
        statement,
        htmlAttributes: {
          class: "survey--comment",
        },
        sections: [
          {
            order: 1,
            type: "text",
            key: "comment",
            maxLength: 1000,
            title: commentTitle,
            description: commentDescription,
            placeholder: t("comment.placeholder"),
            aiResponseTexts,
            required: false,
          },
          {
            order: 98,
            type: "hidden",
            key: "opinion_bucket",
            dependsOn: ["opinion"],
            computeValue: (treesponse) => {
              const value = treesponse.getAnswer("opinion");
              if (["1", "2"].includes(value)) return "1_2";
              if (["4", "5"].includes(value)) return "4_5";
              return value;
            },
          },
        ],
      },
      {
        key: "name",
        order: 4,
        component: PageComponent,
        title: t(["name", "intro", "title"]),
        description: t(["name", "intro", "description"]),
        statement,
        htmlAttributes: {
          class: "survey--inputs",
        },
        sections: [
          {
            order: 1,
            type: "string",
            key: "firstName",
            title: t(["name", "firstName", "title"]),
            required: true,
            defaultValue: () => getPreviousAnswer("firstName"),
            placeholder: t(["name", "firstName", "placeholder"]),
          },
          {
            order: 2,
            type: "string",
            key: "lastName",
            title: t(["name", "lastName", "title"]),
            required: true,
            defaultValue: () => getPreviousAnswer("lastName"),
            placeholder: t(["name", "lastName", "placeholder"]),
          },
          {
            order: 3,
            type: "hidden",
            key: "completed",
            defaultValue: true,
          },
        ],
      },
      {
        key: "organisation",
        order: 5,
        component: PageComponent,
        title: t(["organisation", "intro", "title"]),
        description: t(["organisation", "intro", "description"]),
        htmlAttributes: {
          class: "survey--inputs",
        },
        // We skip this page if the user answered the same name as their previous answer
        skipIf: (treesponse) => {
          return ["firstName", "lastName"].every(
            (key) => treesponse.getAnswer(key) === getPreviousAnswer(key),
          );
        },
        sections: [
          {
            order: 1,
            type: "string",
            key: "org_name",
            title: t(["sociodemo", "org_name", "title"]),
            defaultValue: () => getPreviousAnswer("org_name"),
            required: true,
            placeholder: t(["sociodemo", "org_name", "placeholder"]),
          },
          {
            order: 2,
            type: "string",
            key: "org_function",
            title: t(["sociodemo", "org_function", "title"]),
            defaultValue: () => getPreviousAnswer("org_function"),
            required: false,
            placeholder: t(["sociodemo", "org_function", "placeholder"]),
          },
        ],
      },
      {
        key: "like",
        order: 6,
        component: PageComponent,
        statement,
        title: t(["like", "title"]),
        description: t(["like", "description"]),
        htmlAttributes: {
          class: "survey--inputs",
        },
        skipIf: () => likeOptionsCount !== null && likeOptionsCount < 2,
        sections: [
          {
            type: "like",
            order: 1,
            key: "like",
            aggregatorSettings: {
              url: `${aggregatorURL}/csmart-community`,
              projectKey: import.meta.env.VITE_AGGREGATOR_KEY,
            },
            onOptionsLoaded: (options) => {
              likeOptionsCount = options.length;
            },
          },
        ],
      },
      {
        key: "optin",
        order: 7,
        component: PageComponent,
        title: t(["optin", "intro", "title"]),
        htmlAttributes: {
          class: "survey--inputs",
        },
        // We only show the opt in page if the user has not encountered it before
        skipIf: (treesponse) => {
          return ["firstName", "lastName"].every(
            (key) => treesponse.getAnswer(key) === getPreviousAnswer(key),
          );
        },
        sections: [
          {
            type: "checkbox",
            order: 1,
            title: t(["optin", "optinNewsletter", "title"]),
            key: "optinNewsletter",
          },
          {
            type: "checkbox",
            order: 2,
            title: t(["optin", "optinSmartRockCafe", "title"]),
            key: "optinSmartRockCafe",
          },
          {
            type: "checkbox",
            order: 3,
            title: t(["optin", "optinHelpSolution", "title"]),
            key: "optinHelpSolution",
          },
          {
            type: "email",
            order: 4,
            skipIf: (treesponse) =>
              treesponse.getAnswer("optinNewsletter") !== true &&
              treesponse.getAnswer("optinSmartRockCafe") !== true &&
              treesponse.getAnswer("optinHelpSolution") !== true,
            dependsOn: [
              "optinNewsletter",
              "optinSmartRockCafe",
              "optinHelpSolution",
            ],
            title: t(["optin", "email", "title"]),
            key: "email",
            customValidations: [
              (treesponse, _key, value) => {
                if (
                  (treesponse.getAnswer("optinNewsletter") ||
                    treesponse.getAnswer("optinSmartRockCafe") ||
                    treesponse.getAnswer("optinHelpSolution")) &&
                  (value === null || value === undefined || value.trim() === "")
                ) {
                  return [false, t(["optin", "email", "required"])];
                }

                return [true, null];
              },
            ],
          },
        ],
      },
      {
        key: "next",
        order: 8,
        component: PageComponent,
        title: t(["next", "intro", "title"]),
        description: t([
          "next",
          "intro",
          availableStatements.length > 0
            ? "description"
            : "description_all_done",
        ]),
        sections: [
          {
            type: "content",
            key: "nextStatements",
            component: NextStatements,
            order: 1,
            availableStatements,
          },
          {
            type: "content",
            key: "moreInfoLink",
            component: MoreInfoLink,
            order: 2,
          },
        ],
        showNext: false,
        htmlAttributes: {
          class: "survey--compact",
        },
      },
    ],
  };
}
